import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { favoritesQueryKeys, getFavoritesApi } from '@/entities/favorites';
import { productPageQueryKeys } from '@/entities/product/model/queries';
import { User, useUserData } from '@/entities/user';
import { mergeGuestFavoritesAfterAuth } from '@/features/favorites/lib/mergeGuestFavoritesAfterAuth';
import { useAuthFavoriteStore } from '@/features/favorites/model/authFavoriteStore';
import { useFavoritePendingUiStore } from '@/features/favorites/model/favoritePendingUiStore';
import { useUserStore } from '@/features/user';

export const useSession = (enabled = false) => {
  const { setUser } = useUserStore();
  const { data, error, refetch, isFetched } = useUserData(enabled);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isFetched || error) {
      return;
    }

    if (!data) {
      setUser(null);
      useAuthFavoriteStore.getState().clear();

      return;
    }

    setUser(data as User);

    void (async () => {
      try {
        await mergeGuestFavoritesAfterAuth();
        const fav = await getFavoritesApi();
        useAuthFavoriteStore.getState().setFromProducts(fav.products ?? []);
      } catch {
        useAuthFavoriteStore.getState().clear();
      }

      useFavoritePendingUiStore.getState().refresh();

      queryClient.invalidateQueries({
        queryKey: favoritesQueryKeys.list(),
      });
      queryClient.invalidateQueries({
        queryKey: productPageQueryKeys.all,
      });
    })();
  }, [data, isFetched, error, setUser, queryClient]);

  return { data, refetch, isFetched };
};
